<template>
  <div class="synthese__container">
    <SyntheseSection
      title="L'employeur"
      :items="employerDetails"
      class="col-14 col-sm-36"
    />

    <SyntheseSection
      title="Le salarié(e)"
      :items="salarieDetails"
      class="col-14 col-sm-36"
    />

    <SyntheseSection
      title="Le tuteur au sein de l'établissement employeur"
      :items="tuteurEtablissementDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
     v-if ="estTT &&  model?.typePublic === 'Interimaire' && (isNotNullUndefinedOrEmptyString(model?.tutorat?.tuteurEntrepriseUtilisatrice?.nom) ||isNotNullUndefinedOrEmptyString(model?.tutorat?.tuteurEntrepriseUtilisatrice?.prenom))"
      title="Le tuteur au sein de l’entreprise utilisatrice"
      :items="tuteurEntrepriseDetail"
      class="col-14 col-sm-36"
    />

    <SyntheseSection
      title="Contrat"
      :items="contratDetail"
      class="col-14 col-sm-36"
    />

    <SyntheseSection
      title="Formation"
      :items="formationDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-for="(formationInterne, index) in formationsInterne"
      :key="index"
      title="Formation interne"
      :items="formationInterne"
    />

    <SyntheseSection
      v-for="(formationExterne, index) in formationsExterne"
      :key="index"
      title="Formation externe"
      :items="formationExterne"
    />
  </div>
  <FormContainer>
    <template #content>
      <div class="col-36">
        <WrapperCheckbox
          id="field_unknown1"
          v-model="attestationSurHonneurModel"
          :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
          label="J’atteste sur l’honneur l’exactitude des données saisies."
          :readonly
          class="wrapper-width-fit"
        />
      </div>
      <div class="col-36">
        <WrapperCheckbox
          id="field_unknown2"
          v-model="acceptationCgModel"
          :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
          :readonly
        >
          <BasicText style="flex: 1">
            J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir
            pris connaissance de celles-ci via le lien suivant : «
            <BasicLink
              label="Conditions générales - Akto"
              @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
            />
          </BasicText>
          »
        </WrapperCheckbox>
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_240"
          v-model="lieuSignatureModel"
          label="Fait à"
          :required="meta.lieuSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.lieuSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          hint="50 caractères maximum"
          :maxlength="50"
          :readonly
        />
        <WrapperInputDate
          id="field_241"
          v-model="dateSignatureModel"
          label="Fait le"
          :required="meta.dateSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.dateSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          :maxlength="50"
          :readonly
        />
        <WrapperInput
          id="field_unknown"
          v-model="commentaireModel"
          label="Commentaires (infos complémentaires...)"
          :required="meta.commentaire?.$status === PropertyStatus.Required"
          :alertLabel="meta.commentaire?.$messageErreurDirty!"
          class="col-36"
          deletable
          hint="256 caractères maximum"
          :maxlength="256"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { LovDTO } from '@/features/api/extranet/dto/Lov'
import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
import { FormContainer, SyntheseSection } from '@/features/interface/demande/shared'
import { getLabelBoolean } from '@/features/interface/shared/tools'
import {
  dateFormatter,
  formatNumber,
  isDate,
  isNotNullOrUndefined,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
} from '@/features/shared/tools'
import { redirectExterne } from '@/features/shared/tools/url'
import { inputDureeToString } from '@designSystem/index'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useDemandePROAStore } from '../../useDemandePROAStore'

  const store = useDemandePROAStore()
  const { setupFieldWithKey } = store
  const { readonly, meta, estTT } = storeToRefs(store)
  const { model, lov } = useDemandePROAStore()

  const lieuSignatureModel = setupFieldWithKey<string>('lieuSignature', true)
  const commentaireModel = setupFieldWithKey<string>('commentaire', true)
  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur')
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg')
  const dateSignatureModel = setupFieldWithKey<string>('dateSignature', true)

  const getLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code?: T | null) => {
    if (isNullOrUndefined(code)) return ''
    return lovDTO.find((item) => item.code === code)?.intitule ?? ''
  }
  /*
  const getDynamicLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code: T | null | undefined) =>
    getLovIntitule(lovDTO, code)?.split('-')?.at(1) ?? ''
  */
  const getDateIntitule = (value: any) => (isDate(value) ? dateFormatter(value) : '')

  const employerDetails = computed(() => ({
    'Nom et prénom ou dénomination': model?.employeur?.etablissement?.denomination,
    Siret: model?.employeur?.etablissement?.siret?.valeur,
    'IDCC applicable': model?.employeur?.etablissement?.idcc?.code,
    Email: model?.employeur?.contact?.email,
    'N° et Voie': model?.employeur?.etablissement?.adresse?.voie,
    Complément: model?.employeur?.etablissement?.adresse?.complement,
    'CP Ville': `${model?.employeur?.etablissement?.adresse?.codePostal} ${model?.employeur?.etablissement?.adresse?.commune} `,
  }))

  const salarieDetails = computed(() => ({
    'Nom de naissance': model?.salarie?.nomNaissance,
    "Nom d'usage": model?.salarie?.nom,
    Prénom: model?.salarie?.prenom,
    'Date de naissance': getDateIntitule(model?.salarie?.dateNaissance),
    Email: model?.salarie?.email ?? '',
  }))

  const tuteurEtablissementDetail = computed(() => ({
    'Nom complet': [model?.tutorat?.tuteurEntrepriseEmployeur?.nom, model?.tutorat?.tuteurEntrepriseEmployeur?.prenom]
      .filter(isNotNullUndefinedOrEmptyString)
      .join(' '),
    'Date de naissance': getDateIntitule(model?.tutorat?.tuteurEntrepriseEmployeur?.dateNaissance),
  }))

  const tuteurEntrepriseDetail = computed(() => ({
    'Nom complet': [model?.tutorat?.tuteurEntrepriseUtilisatrice?.nom, model?.tutorat?.tuteurEntrepriseUtilisatrice?.prenom]
      .filter(isNotNullUndefinedOrEmptyString)
      .join(' '),
    'Date de naissance': getDateIntitule(model?.tutorat?.tuteurEntrepriseUtilisatrice?.dateNaissance),
  }))
  const contratDetail = computed(() => ({
    'Emploi occupé': model?.contrat?.emploiOccupe,
    'Date de début du contrat': getDateIntitule(model?.contrat?.dateDebutExecutionContrat),
    'Objectif de la PRO-A': getLovIntitule(lov.ProaVise, model?.contrat?.proaVise),
    'Emploi visé par la PRO-A': model?.contrat?.emploiVise,
    'Date de début de la PRO-A': getDateIntitule(model?.contrat?.dateDebutExecutionProA),
    'Date de fin de la PRO-A': getDateIntitule(model?.contrat?.dateFinExecutionProA),
    'Durée hebdomadaire': inputDureeToString(model?.contrat?.dureeHebdomadaire),
  }))

  const formationDetail = computed(() => ({
    'Intitulé de la formation': model?.formation?.intitule,
    'Type de certification visée': getLovIntitule(lov.TypeQualification, model?.formation?.typeQualification),
    'RNCP / Cléa': isNotNullOrUndefined(model?.formation?.rncp)
      ? `${model!.formation.rncp.code} - ${model.formation.rncp.intitule}`
      : isNotNullOrUndefined(model?.formation?.repertoireSpecifique)
        ? `${model.formation.repertoireSpecifique.code} - ${model.formation.repertoireSpecifique.intitule}`
        : '',
    'Certification visée': getLovIntitule(lov.Diplome, model?.formation?.certificationVisee),
  }))

  const modules = model?.formation?.modules ?? []

  type FormationData = {
    [key: string]: string
  }

  const formationsInterne = computed((): FormationData[] => {
    return modules
      .filter((module) => module.typeModule === 'Interne')
      .map((module) => ({
        'Date de début': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Durée de la formation': module?.duree ? `${module?.duree}h` : '',
        'Coût formation': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })

  const formationsExterne = computed((): FormationData[] => {
    return modules
      .filter((module) => module.typeModule === 'Externe')
      .map((module) => ({
        'Nom de l’organisme de formation': module?.etablissementPrestataire?.denomination ?? '',
        'Date de début': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Durée de la formation': module?.duree ? `${module?.duree}h` : '',
        "Paiement direct à l'organisme de formation": getLabelBoolean(module.avecSubrogation!) ?? '',
        'Coût pédagogique': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })
</script>

<style scoped lang="less">
  .synthese {
    &__bloc {
      margin: @spacing-20 0;
      display: grid;
      grid-template-columns: repeat(36, 1fr);
      grid-row-gap: @spacing-20;
      grid-column-gap: 2%;
    }
  }
</style>

<style scoped lang="less">
  .synthese__container {
    width: 90%;
    align-self: center;
    margin: @spacing-20 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 20px;

    > div {
      width: calc(45% - 10px);
      border-radius: @border-radius-l;

      @media (max-width: @breakpoint-tablet) {
        width: 100%;
      }
    }

    @container (max-width: @breakpoint-mobile) {
      width: 100%;
    }
  }

  .element {
    &__header {
      display: flex;
      font-weight: bold;
      padding-bottom: @spacing-10;
      border-bottom: 1px solid @secondary-600;
    }

    &__row {
      flex: 1;
      display: flex;
      padding: @spacing-5 0;
      border-bottom: 1px solid #e0e0e0;
    }

    &__header,
    &__row {
      > span {
        flex: 1;
        height: 20px;
        padding: 0 @spacing-5;
        text-align: left;
      }
    }
  }
</style>
